import { useEffect, useMemo, useState } from 'react'

import styled from 'styled-components'
import create from 'zustand'
import { animated, config, useIsomorphicLayoutEffect, useTransition } from '@react-spring/web'
import { useConnectModal } from '@rainbow-me/rainbowkit'

import { PageView } from 'components/core/layout/FeaturedItemPage'
import MintScripts from 'components/mint/gobbler/GobblerMintAnimationScripts'

import {
  useNumberOfGobblersAwaitingReveal,
  useNumberOfGobblersUserIsAwaitingReveal,
} from 'hooks/mint/useTransformedRevealData'
import { DEFAULT_CHAIN_ID } from 'config'
import useContracts from 'hooks/mint/useContracts'
import { useSmartContractReadCall } from 'hooks/react-query-typechain/useSmartContractReadCall'
import { useAccount, useNetwork, useSwitchNetwork, useSigner } from 'wagmi'

import { useFormattedGooAndGobblerInfo } from 'hooks/gobblers/useGooBalance'
import { useMerkleProofData } from 'hooks/mint/useMerkleProofData'
import { PrimaryButton } from 'components/core/Buttons'
import { TransactionError } from 'hooks/react-query-typechain/TransactionError'
import { useSmartContractTransaction } from 'hooks/react-query-typechain/useSmartContractTransaction'
import { useIsMounted } from 'hooks/util/useIsMounted'
import MiddleTimer from 'components/mint/gobbler/MiddleTimer'
import { BigNumber } from 'ethers'
import { ArtGobblers } from 'contracts'
import useIsMobile from 'hooks/util/useIsMobile'
import useWindowSize from 'hooks/util/useWindowSize'
import { useInterval } from 'hooks/util/useInterval'
import { useNftsInWalletByCollection } from 'hooks/nfts/useNftsInWallet'
import { cdnRoot } from 'utils/cdn'
import { NextSeo } from 'next-seo'
// import { useGooAmountInterval } from 'hooks/gobblers/useGooVirtualBalance'

export enum GobblerMintColumnStatus {
  GOO_MINT,
  CANT_MINT,
  ALLOWLIST_MINT,
  COMING_SOON,
  LOADING,
}

enum MintStates {
  idle = 'idle',
  submitted = 'submitted',
  mined_and_finishing_animations = 'mined_and_finishing_animations',
  mined = 'mined',
  error = 'error',
}

const useGobblerMintAnimationCoordinator = create(() => ({ frame: 0, isReady: false }))

// @ts-ignore TODO(johnrjj) - Type this in the global typings
global.useGobblerMintAnimationCoordinator = useGobblerMintAnimationCoordinator

function useDidEndInitialMint(gobblerContract: ArtGobblers | undefined) {
  const { data: gobblerMintStartRaw } = useSmartContractReadCall(gobblerContract, 'mintStart')

  const gobblerDidEndInitialMint = useMemo(() => {
    const gobblerMintStart = gobblerMintStartRaw ? new Date(gobblerMintStartRaw.toNumber()) : null

    if (!gobblerMintStart) {
      return null
    }
    const end = new Date(gobblerMintStart.getTime() + 24 * 60 * 60 * 1000)
    // if (end < new Date()) {
    if (end > new Date()) {
      return true
    }
    return false
  }, [gobblerMintStartRaw])

  return gobblerDidEndInitialMint
}

function useMintAnimations(mintingStatus: MintStates, columnStatus: GobblerMintColumnStatus) {
  const curFrame = useGobblerMintAnimationCoordinator((s) => s.frame)
  const isReady = useGobblerMintAnimationCoordinator((s) => s.isReady)

  const currentlyMinting = mintingStatus === MintStates.submitted

  let [didInit, setDidInit] = useState<boolean>(false)

  const isIdle = mintingStatus === MintStates.idle
  useEffect(() => {
    if (!isReady) {
      return
    }
    // Don't update reactively/declratively if we're in the minting flow
    if (!isIdle) {
      return
    }

    if (columnStatus === GobblerMintColumnStatus.GOO_MINT) {
      ;(global as any).mintGobblerStage.getChildAt(0).gotoAndStop(0)
    } else if (columnStatus === GobblerMintColumnStatus.ALLOWLIST_MINT) {
      ;(global as any).mintGobblerStage.getChildAt(0).gotoAndStop(0)
    } else if (columnStatus === GobblerMintColumnStatus.COMING_SOON) {
      ;(global as any).mintGobblerStage.getChildAt(0).gotoAndPlay('vamp')
    }

    setDidInit(true)
  }, [isReady, columnStatus, didInit, isIdle])

  useIsomorphicLayoutEffect(() => {
    const firstGooFrame = 52
    const lastGooFrame = 80

    const lastVampFrame = 202 - 1

    if (currentlyMinting && curFrame >= lastGooFrame) {
      ;(global as any).mintGobblerStage.getChildAt(0).gotoAndPlay(firstGooFrame)
    }
    // If we play the animation, just keep looping the vamp
    else if (curFrame >= lastVampFrame) {
      ;(global as any).mintGobblerStage.getChildAt(0).gotoAndPlay('vamp')
    }
  }, [curFrame])

  // Initialize gobbler animation canvas
  useEffect(() => {
    const tryInit = (delay = 2) => {
      setTimeout(() => {
        try {
          ;(global as any).initMintGobblerAnimation()
        } catch (e) {
          tryInit(delay * delay)
        }
      }, delay)
    }

    tryInit(200)
  }, [])
}

function useTransform() {
  const windowSize = useWindowSize()
  const { width, height } = windowSize

  const [translateY, setTranslateY] = useState(0)
  const [scale, setScale] = useState(0)

  const calculateDimensions = () => {
    const mintMiddleContainer = document.getElementById('mint-middle-container')
    if (!mintMiddleContainer || !height || !width) {
      return
    }
    const deadSpacePct = 60 / 834
    const itemHeight = mintMiddleContainer.getBoundingClientRect().height

    let topOffset = 0
    if (width > 1900) {
      topOffset = 0
    }

    let scaleHeight = 1150
    if (width > 1900) {
      scaleHeight = 1050
    }

    let newTranslateY = -deadSpacePct * itemHeight + topOffset
    let newScale = height / scaleHeight

    if (width < 600) {
      newScale = 0.5
      newTranslateY = -deadSpacePct * itemHeight + 84
    }

    if (newTranslateY !== translateY || newScale !== scale) {
      setScale(newScale)
      setTranslateY(newTranslateY)
    }
  }

  useInterval(calculateDimensions, 125)

  return `translateY(${translateY}px) scale(${scale})`
}

const GobblerMintPage = () => {
  const [mintingStatus, setMintingStatus] = useState<MintStates>(MintStates.idle)
  const [showReveal, setShowReveal] = useState(false)
  const [columnStatusOverride, setColumnStatusOverride] = useState<GobblerMintColumnStatus | null>(
    null,
  )

  const chainId = DEFAULT_CHAIN_ID
  const [gobblerContract, gooContract] = useContracts(chainId)
  const { address } = useAccount()
  const { data: signer } = useSigner()

  const { data: gobblerMintStartRaw } = useSmartContractReadCall(gobblerContract, 'mintStart')

  const hasMintStarted = useMemo(() => {
    const gobblerMintStart = gobblerMintStartRaw ? new Date(gobblerMintStartRaw.toNumber()) : null

    if (!gobblerMintStart) {
      return null
    }
    const start = new Date(gobblerMintStart.getTime() * 1000)
    if (start < new Date()) {
      return true
    }
    return false
  }, [gobblerMintStartRaw])

  // Reveal data
  const gobblersRevealDataQuery = useSmartContractReadCall(gobblerContract, 'gobblerRevealsData')

  const gobblerDidEndInitialMint = useDidEndInitialMint(gobblerContract)

  // Current gobbler id
  const currentNonLegendaryIdQuery = useSmartContractReadCall(
    gobblerContract,
    'currentNonLegendaryId',
  )

  // Current gobbler id
  // const merkleRootQuery = useSmartContractReadCall(gobblerContract, 'merkleRoot')

  const unrevealedAmountOfGobblers = useNumberOfGobblersAwaitingReveal(
    gobblersRevealDataQuery.data,
    currentNonLegendaryIdQuery.data,
  )
  const unrevealedGobblerIdsForUserQuery = useNumberOfGobblersUserIsAwaitingReveal(
    gobblersRevealDataQuery.data,
    currentNonLegendaryIdQuery.data,
  )

  // const res = useTransformedGobblerRevealData()
  const revealData = gobblersRevealDataQuery.data

  const merkleProofData = useMerkleProofData(address)
  const isAddressOnMintAllowList = merkleProofData && merkleProofData.data?.valid

  const hasWalletAlreadyRedeemedQuery = useSmartContractReadCall(
    gobblerContract,
    'hasClaimedMintlistGobbler',
    { callArgs: [address!], enabled: !!address },
  )

  const hasWalletAlreadyRedeemed =
    hasWalletAlreadyRedeemedQuery.data === undefined ? true : hasWalletAlreadyRedeemedQuery.data

  const gobblersOwned = useNftsInWalletByCollection(address, gobblerContract?.address, chainId)

  const allowMintInfo = {
    hasWalletAlreadyRedeemedQuery,
    isAddressOnMintAllowList,
    hasWalletAlreadyRedeemed,
    isLoading: hasWalletAlreadyRedeemedQuery.isLoading,
  }

  const gobblerMintPriceInGooResult = useSmartContractReadCall(gobblerContract, 'gobblerPrice')

  const gooVirtualBalanceQuery = useSmartContractReadCall(gobblerContract, 'gooBalance', {
    callArgs: [address!],
    enabled: !!address,
  })

  const gooPhysicalBalanceQuery = useSmartContractReadCall(gooContract, 'balanceOf', {
    callArgs: [address!],
    enabled: !!address,
  })

  const insufficientVirtualBalance =
    gooVirtualBalanceQuery.data && gobblerMintPriceInGooResult?.data
      ? gobblerMintPriceInGooResult.data.gt(gooVirtualBalanceQuery.data ?? 0)
      : true

  const insufficientPhysicalBalance = Boolean(
    gobblerMintPriceInGooResult.data?.gt(gooPhysicalBalanceQuery.data ?? 0),
  )

  const insufficientBalance = insufficientVirtualBalance && insufficientPhysicalBalance

  const mounted = useIsMounted()
  const isLoading =
    !mounted ||
    allowMintInfo.isLoading ||
    (address && unrevealedGobblerIdsForUserQuery === undefined)

  let columnStatusDerived = GobblerMintColumnStatus.GOO_MINT
  if (!allowMintInfo.hasWalletAlreadyRedeemed && allowMintInfo.isAddressOnMintAllowList) {
    columnStatusDerived = GobblerMintColumnStatus.ALLOWLIST_MINT
  }

  if (!gobblerDidEndInitialMint && !allowMintInfo.isAddressOnMintAllowList) {
    // columnStatusDerived = GobblerMintColumnStatus.CANT_MINT
  }
  if ((unrevealedGobblerIdsForUserQuery?.length ?? 0) > 0) {
    columnStatusDerived = GobblerMintColumnStatus.COMING_SOON
  }

  if (isLoading) {
    columnStatusDerived = GobblerMintColumnStatus.LOADING
  }

  if (columnStatusOverride === GobblerMintColumnStatus.GOO_MINT) {
    columnStatusDerived = columnStatusOverride
  }

  useMintAnimations(mintingStatus, columnStatusDerived)

  const transitions = useTransition(columnStatusDerived, {
    from: { opacity: 0, y: 10 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: 10 },
    key: columnStatusDerived,
    config: config.gentle,
  })

  const [gooBalanceFmt, gobblerPriceInGoopFmt] = useFormattedGooAndGobblerInfo()

  const network = useNetwork()
  const { openConnectModal } = useConnectModal()

  const wrongChain = chainId !== network.chain?.id.toString()

  const switchNetwork = useSwitchNetwork()

  const isUserActivelyMintingPage = mintingStatus === 'submitted'

  const mintPageFromMintlistMutation = useSmartContractTransaction(
    gobblerContract,
    'claimGobbler',
    signer ?? undefined,
    {
      onError(e: TransactionError) {
        setMintingStatus(MintStates.error)
        ;(global as any).mintGobblerStage.getChildAt(0).gotoAndStop(0)
        if ((e as any).code === 'ACTION_REJECTED') {
          return
        }
      },
      async onTransactionMined(transactionReceipt, callArgs, transactionStatus) {
        // setMintingStatus(MintStates.mined_and_finishing_animations)
        setMintingStatus(MintStates.mined)
        setColumnStatusOverride(GobblerMintColumnStatus.COMING_SOON)
        // setTimeout(() => {
        // }, 1)
        // setMintTx(transactionReceipt.transactionHash)
        // await erc20InputAllowance.refetch()
        const refetchGooBalancePromise = gooPhysicalBalanceQuery.refetch()
        const refetchGooVirtualBalancePromise = gooVirtualBalanceQuery.refetch()
        const refetchGobblerMintPricePromise = gobblerMintPriceInGooResult.refetch()
        const refetchGobblersInWalletPromise = gobblersOwned.mutate()
        const refetchGlobalRevealDataPromise = gobblersRevealDataQuery.refetch()
        await refetchGooBalancePromise
        await refetchGobblerMintPricePromise
        await refetchGooVirtualBalancePromise
        await refetchGobblersInWalletPromise
        await refetchGlobalRevealDataPromise
      },
      onTransactionSubmitted(transaction, callArgs) {
        setMintingStatus(MintStates.submitted)
        ;(global as any).mintGobblerStage.getChildAt(0).gotoAndPlay(0)
      },
    },
  )

  const mintPageMutation = useSmartContractTransaction(
    gobblerContract,
    'mintFromGoo',
    signer ?? undefined,
    {
      onError(e: TransactionError) {
        setMintingStatus(MintStates.error)
        if ((e as any).code === 'ACTION_REJECTED') {
          return
        }
      },
      async onTransactionMined(transactionReceipt, callArgs, transactionStatus) {
        // transactionStatus
        // transactionReceipt.
        setMintingStatus(MintStates.mined_and_finishing_animations)
        setTimeout(() => {
          setMintingStatus(MintStates.mined)
        }, 1000)
        // setMintTx(transactionReceipt.transactionHash)
        // await erc20InputAllowance.refetch()
        const refetchGooBalancePromise = gooPhysicalBalanceQuery.refetch()
        const refetchGobblerMintPricePromise = await gobblerMintPriceInGooResult.refetch()
        const refetchGobblersInWalletPromise = gobblersOwned.mutate()
        const refetchGlobalRevealDataPromise = gobblersRevealDataQuery.refetch()
        const refetchGooVirtualBalancePromise = gooVirtualBalanceQuery.refetch()

        await refetchGooBalancePromise
        await refetchGobblerMintPricePromise
        await refetchGobblersInWalletPromise
        await refetchGlobalRevealDataPromise
        await refetchGooVirtualBalancePromise
      },
      onTransactionSubmitted(transaction, callArgs) {
        setMintingStatus(MintStates.submitted)
        ;(global as any).mintGobblerStage.getChildAt(0).gotoAndPlay(0)
      },
    },
  )

  const shouldMintFromMintlist = columnStatusDerived === GobblerMintColumnStatus.ALLOWLIST_MINT

  const DerivedMintButton = useMemo(() => {
    let mintText = 'Mint Gobbler'
    if (columnStatusDerived === GobblerMintColumnStatus.ALLOWLIST_MINT) {
      mintText = 'Mint for free'
    }

    if (!address) {
      return (
        <PrimaryButton buttonType="success" onPress={openConnectModal}>
          Connect wallet
        </PrimaryButton>
      )
    }
    if (wrongChain) {
      return (
        <PrimaryButton
          buttonType="success"
          onPress={() => switchNetwork.switchNetwork?.(parseInt(chainId, 10))}
        >
          Switch network
        </PrimaryButton>
      )
    }

    if (isUserActivelyMintingPage) {
      return (
        <PrimaryButton buttonType="success" isDisabled={true}>
          <>Minting</>
        </PrimaryButton>
      )
    }

    if (columnStatusDerived === GobblerMintColumnStatus.COMING_SOON) {
      // TODO: Add real Discord url here!
      return null
      // <PrimaryButton target="_blank" as={'a'} href="https://discord.gg/2Z8Y4Y4">
      //   Join our Discord
      // </PrimaryButton>
    }

    if (columnStatusDerived === GobblerMintColumnStatus.CANT_MINT) {
      // TODO: Add real Discord url here!
      return null
      // <PrimaryButton buttonType="success" isDisabled>
      //   Not on mintlist
      // </PrimaryButton>
    }

    if (insufficientBalance && !shouldMintFromMintlist && !isUserActivelyMintingPage) {
      return (
        <PrimaryButton buttonType="success" isDisabled>
          Insufficient GOO
        </PrimaryButton>
      )
    }

    if (gooPhysicalBalanceQuery.error) {
      return (
        <PrimaryButton buttonType="success" isDisabled>
          Error loading balance
        </PrimaryButton>
      )
    }
    if (!hasMintStarted) {
      return (
        <PrimaryButton buttonType="success" isDisabled>
          Mint not started
        </PrimaryButton>
      )
    }
    if (!gobblerMintPriceInGooResult.data) {
      return (
        <PrimaryButton buttonType="success" isDisabled>
          Loading...
        </PrimaryButton>
      )
    }

    if (
      mintingStatus === MintStates.mined_and_finishing_animations ||
      mintingStatus === MintStates.mined
    ) {
      return (
        <PrimaryButton buttonType="success" isDisabled={true}>
          <>Gobbler Summoned</>
        </PrimaryButton>
      )
    }

    // TODO: Apparently we took this out
    // if (mintingStatus === MintStates.mined) {
    //   return (
    //     <Link
    //       passHref={true}
    //       href={{
    //         pathname: '/gobbler/[gobblerId]',
    //         query: {
    //           gobblerId: '',
    //         },
    //       }}
    //     >
    //       <PrimaryButton as={'a'} buttonType="success" isDisabled={true}>
    //         <>View Gobbler</>
    //       </PrimaryButton>
    //     </Link>
    //   )
    // }

    return (
      <PrimaryButton
        buttonType="success"
        onPress={async () => {
          const maybeProof = merkleProofData?.data?.proof || []

          if (shouldMintFromMintlist && maybeProof) {
            const mintFromMintListTx = await mintPageFromMintlistMutation.mutateAsync([maybeProof])
          } else {
            const mintFromGooBalance = await mintPageMutation.mutateAsync([
              gobblerMintPriceInGooResult.data,
              insufficientPhysicalBalance,
            ])
          }
        }}
      >
        {mintText}
      </PrimaryButton>
    )
  }, [
    address,
    chainId,
    columnStatusDerived,
    gobblerMintPriceInGooResult.data,
    gooPhysicalBalanceQuery.error,
    hasMintStarted,
    insufficientBalance,
    insufficientPhysicalBalance,
    isUserActivelyMintingPage,
    merkleProofData?.data?.proof,
    mintPageFromMintlistMutation,
    mintPageMutation,
    mintingStatus,
    openConnectModal,
    shouldMintFromMintlist,
    switchNetwork,
    wrongChain,
  ])

  const timeStamp = revealData?.[1]?.toNumber()

  useEffect(() => {
    if (!timeStamp) {
      return
    }
    if (new Date(timeStamp).getTime() < new Date().getTime() && !showReveal) {
      setShowReveal(true)
    }
  }, [showReveal, timeStamp])

  const COPY_INFO: Record<GobblerMintColumnStatus, { title: string | null; subtitle: string }> = {
    [GobblerMintColumnStatus.GOO_MINT]: {
      title: 'Mint a Gobbler',
      subtitle: `Use your Goo to teleport an Art Gobbler down to Earth. Current Price: ${gobblerPriceInGoopFmt?.formattedValue} Goo`,
    },
    [GobblerMintColumnStatus.ALLOWLIST_MINT]: {
      title: 'Summon your Gobbler',
      subtitle:
        'Congratulations, human. Click this innocent-looking button to play your role in our invasion of your planet.',
    },
    [GobblerMintColumnStatus.COMING_SOON]: {
      title: null,
      subtitle:
        'Congratulations, human. Your Gobbler is teleporting to Earth, and will arrive at approximately 4:20 PM US Eastern time.',
    },
    [GobblerMintColumnStatus.LOADING]: {
      title: 'Establishing uplink...',
      subtitle: 'Please stand by, human.',
    },
    [GobblerMintColumnStatus.CANT_MINT]: {
      title: "This wallet isn't on the mint list",
      subtitle: 'Starting 11/1 at 4:20pm, anyone can mint a new Gobbler using Goo.',
    },
  }

  let { title, subtitle } = COPY_INFO[columnStatusDerived]
  if (!address) {
    title = 'Are you on the mintlist?'
    subtitle = 'Connect your wallet and summon your gobbler!'
  }

  const isMobile = useIsMobile(821)

  const transform = useTransform()

  const seoTitle = `Summon Your Gobbler | Art Gobblers`
  const seoDescription = `Art Gobblers are the owners of an experimental decentralized art factory dedicated to creating and collecting the coolest art in the universe. Teleport yours to Earth today!`
  const seoImage = `${cdnRoot}/images/gobbler-mint-og.png`

  return (
    <>
      <MintScripts />
      <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/_preloader.gif`}
      />
      <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/minting machine_atlas_1.png`}
      />
      <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/minting machine_atlas_2.png`}
      />
      <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/minting machine_atlas_3.png`}
      />
      <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/minting machine_atlas_4.png`}
      />
      {/* <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/minting machine_atlas_5.png`}
      /> */}
      {/* <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/minting machine_atlas_6.png`}
      /> */}
      {/* <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/minting machine_atlas_7.png`}
      /> */}
      {/* <link
        rel="preload"
        as="image"
        href={`${cdnRoot}/animations/mint-gobbler/images/minting machine_atlas_8.png`}
      /> */}

      <NextSeo
        openGraph={{
          title: seoTitle,
          description: seoDescription,
          images: [
            {
              url: seoImage,
              width: 500,
              height: 500,
              alt: 'Summon your Gobbler - Use the mint machine',
            },
          ],
        }}
        additionalMetaTags={[
          {
            name: 'twitter:image',
            content: seoImage,
          },
        ]}
      ></NextSeo>

      <PageView style={{ maxHeight: '100vh', flexDirection: 'column', display: 'flex' }}>
        <TopImage src="/images/dashboard/factory_top.png" />
        <MiddleContainer id="mint-middle-container" style={{ transform }}>
          <div
            id="mint-gobbler-animation-container"
            style={{
              backgroundColor: 'transparent',
              width: 1440,
              height: 1000,
              position: 'absolute',
              left: 0,
              top: 0,
              zIndex: 2,
            }}
          >
            <MintGobblerCanvas width={1440} height={1000} id="mint-gobbler-canvas" />
            <div
              id="mint-gobbler-dom-overlay-container"
              style={{
                pointerEvents: 'none',
                overflow: 'hidden',
                width: 1440,
                height: 1000,
                position: 'absolute',
                left: 0,
                top: 0,
                display: 'block',
              }}
            />
          </div>

          <div
            id="gobbler-mint-animation-preloader"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              display: 'inline-block',
              // height: 700,
              width: '100%',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                height: '100%',
                verticalAlign: 'middle',
              }}
            ></span>{' '}
            <img
              src={`${cdnRoot}/animations/mint-gobbler/images/_preloader.gif`}
              style={{
                verticalAlign: 'middle',
                maxHeight: '100%',
              }}
            />
          </div>

          <ShadowContainer>
            <svg
              style={{ marginLeft: -72, transform: 'translateY(-32px) scale(1.1)' }}
              width="537"
              height="173"
              viewBox="0 0 537 173"
              fill="none"
            >
              <ellipse cx="268.159" cy="86.2773" rx="268.159" ry="86.2773" fill="#6A2160" />
            </svg>
          </ShadowContainer>
        </MiddleContainer>
        {timeStamp && columnStatusDerived === GobblerMintColumnStatus.COMING_SOON && (
          <MiddleTimerDiv>
            <MiddleTimer
              unrevealedAmountOfGobblers={unrevealedAmountOfGobblers}
              timeStamp={timeStamp}
            />
          </MiddleTimerDiv>
        )}
        {transitions((styles, item) => (
          <AnimatedBottomContainer key={item} style={{ ...styles }}>
            <BottomLeftContainer>
              {<div>{title}</div>}
              {subtitle && <div>{subtitle}</div>}
            </BottomLeftContainer>
            <div style={{ width: 42 }} />
            {DerivedMintButton && (
              <div style={{ width: 280, maxWidth: 280, minWidth: 280 }}>{DerivedMintButton}</div>
            )}
          </AnimatedBottomContainer>
        ))}

        <BackgroundImage
          src={
            isMobile
              ? `${cdnRoot}/images/dashboard/mint_bg_mobile.png`
              : `${cdnRoot}/images/dashboard/mint_bg.png`
          }
        />
      </PageView>
    </>
  )
}

// New

const TopImage = styled.img`
  width: 30vw;
  min-width: 500px;
  object-fit: cover;
  position: absolute;
  left: 55%;
  transform: translateX(-55%);

  top: 0;
  z-index: 7;
`
const MiddleContainer = styled.div`
  z-index: 6;

  height: 1000px;
  position: relative;

  height: 1000px;
  width: 1440px;

  transform-origin: center top;
  min-height: 1000px;
  min-width: 1440px;
`

const ShadowContainer = styled.div`
  width: 1440px;
  height: 1000px;
  position: absolute;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;

  z-index: 1;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
`

const BackgroundImage = styled.img`
  position: absolute;
  height: 100%;
  width: 100%;

  min-height: 100vh;
  min-width: 100vw;

  zindex: 5;
  top: 0;
  left: 0;

  object-fit: cover;
`

const MintGobblerCanvas = styled.canvas`
  position: absolute;
  display: block;
  background-color: transparent;
`

const BottomContainer = styled.div`
  position: absolute;
  bottom: 40px;
  width: calc(100% - 40px);
  border-radius: 178px;
  max-width: 1020px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  height: 136px;
  z-index: 10;

  background: rgba(8, 10, 12, 0.8);
  box-shadow: 0px 24px 60px rgba(0, 0, 0, 0.06), 0px 12px 20px rgba(0, 0, 0, 0.05),
    0px 4px 8px rgba(0, 0, 0, 0.02);
  backdrop-filter: blur(4px);

  padding: 0 48px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: calc(100% - 24px);
    flex-direction: column; 
    bottom: 32px; 
    height: auto;
    border-radius: 40px; 
    padding: 24px 16px;
    gap: 0px;
  `};
`
const AnimatedBottomContainer = animated(BottomContainer)

const BottomLeftContainer = styled.div`
  display: flex;
  flex-direction: column;

  gap: 8px;

  > :first-child {
    font-size: 36px;
    font-weight: 700;

    ${({ theme }) => theme.mediaWidth.upToMedium`
      font-size: 28px;
    `};

    ${({ theme }) => theme.mediaWidth.upToSmall`
      text-align: center; 
    `};
  }

  > :nth-child(2) {
    font-size: 18px;
    font-weight: 400;
    color: #9c9da8;
    max-width: 720px;

    ${({ theme }) => theme.mediaWidth.upToMedium`
      font-size: 14px;
  `};
    ${({ theme }) => theme.mediaWidth.upToSmall`
      text-align: center;
      margin-top: 4px;
      margin-bottom: 16px;
    `};
  }
`

const MiddleTimerDiv = styled.div`
  z-index: 10;
  // transform: translate(0, 75%);
  position: absolute;
  top: 40%;
`

export default GobblerMintPage
